import React, { useEffect, useState, useCallback, useMemo } from 'react';
import i18n from '../i18n';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { SafeHtml } from '../lib/sanitize';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Package, Plus, Pencil, Trash2, Eye, EyeOff, MapPin, Building2, CreditCard, Euro, Mail, RefreshCw, Search, ShieldAlert, FileText, Upload, Link as LinkIcon, Copy, FileEdit, Sparkles } from 'lucide-react';
import RichEmailEditor from '../components/RichEmailEditor';

// ★ 2026-08-03 (Fase 31 B) — Libreria proposte commerciali.
// ★ 2026-08-04 (Fase 33.2/33.4) — PDF allegato + duplica proposta.
// Solo Richard/Eufemia/Morena/Claudia possono creare/modificare.

const COUNTRY_OPTIONS = [
  { value: 'albania', label: 'Albania', flag: '🇦🇱' },
  { value: 'indonesia', label: 'Indonesia', flag: '🇮🇩' },
  { value: 'thailandia', label: 'Thailandia', flag: '🇹🇭' },
];
const PATH_OPTIONS = [
  { value: 'equity', label: 'Equity' },
  { value: 'real_estate', label: 'Real Estate' },
];

// ★ 2026-08-29 (Fase 58.K) — Filtro Progetto: etichette leggibili per slug.
const PROJECT_LABELS = {
  'palm-paradise': 'Palm Paradise',
  'vista-mare': 'Vista Mare',
  'green-paradise-lombok': 'Green Paradise Lombok',
  'la-mar-residence': 'La Mar Residence',
  narea: 'Narea',
  lombok: 'Lombok',
};
const projectLabel = (slug) => PROJECT_LABELS[slug]
  || (slug || '').split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const MAX_PDF_BYTES = 10 * 1024 * 1024;
// Locale date/number in base alla lingua utente
const dtLocale = () => (i18n.language === 'es' ? 'es-ES' : i18n.language === 'en' ? 'en-GB' : 'it-IT');

const EMPTY_FORM = {
  name: '',
  country: 'albania',
  path: 'equity',
  price_eur: '',
  payment_method: '',
  email_subject: '',
  email_body_html: '',
  internal_notes: '',
  is_active: true,
  pdf_ext_link: '',
};

export default function ProposalsLibraryPage() {
  const { user } = useAuth() || {};
  const [items, setItems] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [pathFilter, setPathFilter] = useState('all');
  // ★ 2026-08-29 (Fase 58.K) — Filtro Progetto (project_slug delle proposte unit-linked)
  const [projectFilter, setProjectFilter] = useState('all');
  const [projectOptions, setProjectOptions] = useState([]);
  const [hasGenericProposals, setHasGenericProposals] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);      // null = create new
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [previewProposal, setPreviewProposal] = useState(null);
  const [deletingProposal, setDeletingProposal] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [duplicating, setDuplicating] = useState(null);
  // ★ 2026-08-10 (Fase 36) — Multi-PDF: se in modalità "Nuova proposta"
  // teniamo array di file e link pending; verranno caricati dopo il save.
  const [pendingPdfFiles, setPendingPdfFiles] = useState([]);
  const [pendingExtLinks, setPendingExtLinks] = useState([]);
  const [newExtLinkInput, setNewExtLinkInput] = useState('');
  // ★ 2026-08-10 (Fase 35) — Template email built-in + Google Doc import
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [importDocOpen, setImportDocOpen] = useState(false);
  const [importDocUrl, setImportDocUrl] = useState('');
  const [importingDoc, setImportingDoc] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (countryFilter !== 'all') params.country = countryFilter;
      if (pathFilter !== 'all') params.path = pathFilter;
      if (projectFilter !== 'all') params.project = projectFilter;
      if (!showInactive) params.is_active = true;
      const { data } = await api.get('/proposals-library', { params });
      setItems(data.items || []);
      setCanEdit(!!data.can_edit);
      setProjectOptions(data.projects || []);
      setHasGenericProposals(!!data.has_generic_proposals);
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('library.loadError', 'Errore caricamento libreria proposte'));
    } finally { setLoading(false); }
  }, [countryFilter, pathFilter, projectFilter, showInactive]);

  useEffect(() => { load(); }, [load]);

  // Carica lista template built-in una volta
  useEffect(() => {
    api.get('/proposals-library/_meta/templates')
      .then((r) => setEmailTemplates(r.data.templates || []))
      .catch(() => setEmailTemplates([]));
  }, []);

  const applyTemplate = (tplKey) => {
    const tpl = emailTemplates.find((t) => t.key === tplKey);
    if (!tpl) return;
    const hasContent = (form.email_body_html || '').trim();
    if (hasContent && !window.confirm(i18n.t('library.tplReplaceConfirm', 'Sostituire il contenuto attuale del corpo email con il template "{{label}}"?', { label: tpl.label }))) {
      return;
    }
    setForm((f) => ({
      ...f,
      email_subject: f.email_subject || tpl.subject,
      email_body_html: tpl.body_html,
    }));
    toast.success(i18n.t('library.tplApplied', 'Template "{{label}}" applicato', { label: tpl.label }));
  };

  const importGoogleDoc = async () => {
    const url = (importDocUrl || '').trim();
    if (!url) { toast.error(i18n.t('library.pasteGdoc', 'Incolla il link del Google Doc')); return; }
    setImportingDoc(true);
    try {
      const { data } = await api.post('/proposals-library/import-google-doc', { url });
      setForm((f) => ({ ...f, email_body_html: data.html }));
      toast.success(i18n.t('library.gdocImported', 'Contenuto importato da Google Doc'));
      setImportDocOpen(false);
      setImportDocUrl('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('library.gdocImportError', 'Errore importazione Google Doc'));
    } finally { setImportingDoc(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      (p.name || '').toLowerCase().includes(q)
      || (p.payment_method || '').toLowerCase().includes(q)
      || (p.email_subject || '').toLowerCase().includes(q),
    );
  }, [items, search]);

  const openEdit = (p) => {
    setEditing(p);
    setPendingPdfFiles([]);
    setPendingExtLinks([]);
    setNewExtLinkInput('');
    setForm({
      name: p.name || '',
      country: p.country,
      path: p.path,
      price_eur: String(p.price_eur ?? ''),
      payment_method: p.payment_method || '',
      email_subject: p.email_subject || '',
      email_body_html: p.email_body_html || '',
      internal_notes: p.internal_notes || '',
      is_active: !!p.is_active,
      pdf_ext_link: '',
    });
    setEditorOpen(true);
  };

  const openCreate = () => {
    setEditing(null);
    setPendingPdfFiles([]);
    setPendingExtLinks([]);
    setNewExtLinkInput('');
    setForm(EMPTY_FORM);
    setEditorOpen(true);
  };

  const save = async () => {
    const price = parseFloat(form.price_eur);
    if (!form.name.trim()) { toast.error(i18n.t('library.nameRequired', 'Il nome è obbligatorio')); return; }
    if (Number.isNaN(price) || price < 0) { toast.error(i18n.t('library.priceInvalid', 'Prezzo non valido')); return; }
    if (!form.payment_method.trim()) { toast.error(i18n.t('library.paymentRequired', 'Il metodo di pagamento è obbligatorio')); return; }
    if (!form.email_subject.trim()) { toast.error(i18n.t('library.subjectRequired', "L'oggetto email è obbligatorio")); return; }
    if (!form.email_body_html.trim()) { toast.error(i18n.t('library.bodyRequired', 'Il corpo email è obbligatorio')); return; }

    const payload = {
      name: form.name.trim(),
      country: form.country,
      path: form.path,
      price_eur: price,
      payment_method: form.payment_method.trim(),
      email_subject: form.email_subject.trim(),
      email_body_html: form.email_body_html,
      internal_notes: form.internal_notes || null,
      is_active: form.is_active,
    };
    setSaving(true);
    try {
      let createdOrUpdated;
      if (editing) {
        const r = await api.patch(`/proposals-library/${editing.id}`, payload);
        createdOrUpdated = r.data;
        toast.success(i18n.t('library.updated', 'Proposta aggiornata'));
      } else {
        const r = await api.post('/proposals-library', payload);
        createdOrUpdated = r.data;
        toast.success(i18n.t('library.created', 'Proposta creata'));
      }
      // ★ 2026-08-10 (Fase 36) — Upload di tutti i PDF pending e link pending
      if (createdOrUpdated?.id) {
        for (const file of pendingPdfFiles) {
          try {
            const fd = new FormData();
            fd.append('file', file);
            await api.post(`/proposals-library/${createdOrUpdated.id}/pdf`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          } catch (err) {
            toast.warning(`PDF "${file.name}": ${err?.response?.data?.detail || i18n.t('library.uploadFailed', 'upload fallito')}`);
          }
        }
        for (const url of pendingExtLinks) {
          try {
            await api.post(`/proposals-library/${createdOrUpdated.id}/ext-link`, { url });
          } catch (err) {
            toast.warning(`Link "${url}": ${err?.response?.data?.detail || i18n.t('library.addFailed', 'aggiunta fallita')}`);
          }
        }
        if (pendingPdfFiles.length + pendingExtLinks.length > 0) {
          toast.success(i18n.t('library.pendingSaved', '{{pdfs}} PDF + {{links}} link salvati', { pdfs: pendingPdfFiles.length, links: pendingExtLinks.length }));
        }
      }
      setPendingPdfFiles([]);
      setPendingExtLinks([]);
      setNewExtLinkInput('');
      setEditorOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('library.saveError', 'Errore salvataggio proposta'));
    } finally { setSaving(false); }
  };

  const uploadPdfNow = async (file) => {
    // Immediate upload when editing an existing proposal
    if (!editing?.id) return;
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error(i18n.t('library.onlyPdf', 'Formato non supportato: solo PDF'));
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      toast.error(i18n.t('library.fileTooBig', 'File troppo grande (max 10 MB). Usa un link esterno.'));
      return;
    }
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/proposals-library/${editing.id}/pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(i18n.t('orders.uploaded', '"{{name}}" caricato', { name: file.name }));
      // Refresh both editing state (from server, so pdfs array is fresh) and list
      const { data: fresh } = await api.get(`/proposals-library/${editing.id}`);
      setEditing(fresh);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('orders.uploadError', 'Errore upload PDF'));
    } finally { setUploadingPdf(false); }
  };

  const removePdf = async (pdfId) => {
    if (!editing?.id || !pdfId) return;
    try {
      await api.delete(`/proposals-library/${editing.id}/pdf/${pdfId}`);
      toast.success(i18n.t('library.pdfRemoved', 'PDF rimosso'));
      const { data: fresh } = await api.get(`/proposals-library/${editing.id}`);
      setEditing(fresh);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('orders.removeError', 'Errore rimozione'));
    }
  };

  const addExtLinkNow = async () => {
    const url = (newExtLinkInput || '').trim();
    if (!url) return;
    if (!editing?.id) {
      // Nuova proposta → accumula in pending
      if (pendingExtLinks.includes(url)) {
        toast.info(i18n.t('library.linkAlready', 'Link già aggiunto'));
        return;
      }
      setPendingExtLinks((arr) => [...arr, url]);
      setNewExtLinkInput('');
      return;
    }
    try {
      await api.post(`/proposals-library/${editing.id}/ext-link`, { url });
      toast.success(i18n.t('library.linkAdded', 'Link aggiunto'));
      const { data: fresh } = await api.get(`/proposals-library/${editing.id}`);
      setEditing(fresh);
      setNewExtLinkInput('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('library.genericError', 'Errore'));
    }
  };

  const removeExtLink = async (linkId) => {
    if (!editing?.id || !linkId) return;
    try {
      await api.delete(`/proposals-library/${editing.id}/ext-link/${linkId}`);
      toast.success(i18n.t('library.linkRemoved', 'Link rimosso'));
      const { data: fresh } = await api.get(`/proposals-library/${editing.id}`);
      setEditing(fresh);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('library.genericError', 'Errore'));
    }
  };

  const duplicate = async (p) => {
    setDuplicating(p.id);
    try {
      const { data } = await api.post(`/proposals-library/${p.id}/duplicate`);
      toast.success(i18n.t('library.duplicated', 'Duplicata: "{{name}}"', { name: data.name }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('library.duplicateError', 'Errore duplicazione'));
    } finally { setDuplicating(null); }
  };

  const softDelete = async () => {
    if (!deletingProposal) return;
    try {
      await api.delete(`/proposals-library/${deletingProposal.id}`);
      toast.success(i18n.t('library.deactivated', 'Proposta disattivata'));
      setDeletingProposal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('library.genericError', 'Errore'));
    }
  };

  const countryMeta = (c) => COUNTRY_OPTIONS.find((o) => o.value === c) || { label: c, flag: '' };
  const pathLabel = (p) => (PATH_OPTIONS.find((o) => o.value === p) || {}).label || p;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="proposals-library-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Package className="w-6 h-6 text-[hsl(var(--primary))]" />
          <h1 className="text-2xl font-serif text-[hsl(var(--primary))]">{i18n.t('library.title', 'Libreria proposte')}</h1>
          <Badge variant="outline" className="text-[10px]">
            {items.length} {i18n.t('library.proposalsCount', 'proposte')}
          </Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={load} data-testid="proposals-refresh">
            <RefreshCw className="w-4 h-4 mr-1" />{i18n.t('common.refresh', 'Aggiorna')}
          </Button>
          {canEdit && (
            <Button size="sm" onClick={openCreate} data-testid="proposals-create-btn">
              <Plus className="w-4 h-4 mr-1" />{i18n.t('library.newProposal', 'Nuova proposta')}
            </Button>
          )}
        </div>
      </div>

      {!canEdit && (
        <Card className="border-amber-300 bg-amber-50/40" data-testid="proposals-readonly-notice">
          <CardContent className="p-3 flex items-center gap-2 text-sm">
            <ShieldAlert className="w-4 h-4 text-amber-600" />
            <span>{i18n.t('library.readonlyNotice', 'Puoi consultare la libreria ma non modificarla. Le modifiche sono riservate agli admin CEO.')}</span>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8 h-9"
              placeholder={i18n.t('library.searchPh', 'Cerca per nome, pagamento, oggetto…')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="proposals-search"
            />
          </div>
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="w-[160px] h-9" data-testid="proposals-country-filter">
              <SelectValue placeholder={i18n.t('library.country', 'Paese')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{i18n.t('library.allCountries', 'Tutti i paesi')}</SelectItem>
              {COUNTRY_OPTIONS.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.flag} {c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={pathFilter} onValueChange={setPathFilter}>
            <SelectTrigger className="w-[160px] h-9" data-testid="proposals-path-filter">
              <SelectValue placeholder="Path" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{i18n.t('library.allPaths', 'Tutti i path')}</SelectItem>
              {PATH_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* ★ 2026-08-29 (Fase 58.K) — Filtro Progetto */}
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-[210px] h-9" data-testid="proposals-project-filter">
              <SelectValue placeholder={i18n.t('library.project', 'Progetto')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{i18n.t('library.allProjects', 'Tutti i progetti')}</SelectItem>
              {projectOptions.map((slug) => (
                <SelectItem key={slug} value={slug} data-testid={`proposals-project-option-${slug}`}>
                  {projectLabel(slug)}
                </SelectItem>
              ))}
              {hasGenericProposals && (
                <SelectItem value="__none__" data-testid="proposals-project-option-none">
                  {i18n.t('library.noProject', 'Senza progetto')}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="proposals-show-inactive" />
            {i18n.t('library.showInactive', 'Mostra inattive')}
          </label>
        </CardContent>
      </Card>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-56" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-muted-foreground">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>{i18n.t('library.noMatch', 'Nessuna proposta corrispondente ai filtri.')}</p>
            {canEdit && <Button size="sm" className="mt-3" onClick={openCreate}>{i18n.t('library.createFirst', 'Crea la prima proposta')}</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((p) => {
            const cm = countryMeta(p.country);
            return (
              <Card key={p.id} className={p.is_active ? '' : 'opacity-60 border-dashed'} data-testid={`proposal-card-${p.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base leading-tight">{p.name}</CardTitle>
                    <Badge variant={p.is_active ? 'default' : 'secondary'} className="text-[10px]">
                      {p.is_active ? i18n.t('library.active', 'Attiva') : i18n.t('library.inactive', 'Inattiva')}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs mt-1">
                    <Badge variant="outline" className="text-[11px]">
                      <MapPin className="w-3 h-3 mr-1" />{cm.flag} {cm.label}
                    </Badge>
                    <Badge variant="outline" className="text-[11px]">
                      <Building2 className="w-3 h-3 mr-1" />{pathLabel(p.path)}
                    </Badge>
                    {p.project_slug && (
                      <Badge variant="outline" className="text-[11px] bg-sky-50 border-sky-200 text-sky-800" data-testid={`proposal-project-badge-${p.id}`}>
                        <Package className="w-3 h-3 mr-1" />{projectLabel(p.project_slug)}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 pt-0">
                  <div className="flex items-center gap-1 text-lg font-bold text-emerald-700">
                    <Euro className="w-4 h-4" />
                    {(p.price_eur || 0).toLocaleString(dtLocale(), { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-start gap-1">
                    <CreditCard className="w-3 h-3 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{p.payment_method}</span>
                  </div>
                  <div className="text-xs text-muted-foreground flex items-start gap-1">
                    <Mail className="w-3 h-3 mt-0.5 shrink-0" />
                    <span className="line-clamp-1 italic">{p.email_subject}</span>
                  </div>
                  {((p.pdfs && p.pdfs.length > 0) || (p.ext_links && p.ext_links.length > 0) || p.pdf_url || p.pdf_ext_link) && (
                    <div className="flex items-center gap-1 text-[11px] flex-wrap">
                      {(p.pdfs?.length > 0 || p.pdf_url) && (
                        <Badge variant="outline" className="text-[10px] gap-1 bg-emerald-50 border-emerald-200 text-emerald-800">
                          <FileText className="w-3 h-3" />{p.pdfs?.length || 1} PDF
                        </Badge>
                      )}
                      {(p.ext_links?.length > 0 || p.pdf_ext_link) && (
                        <Badge variant="outline" className="text-[10px] gap-1 bg-sky-50 border-sky-200 text-sky-800">
                          <LinkIcon className="w-3 h-3" />{p.ext_links?.length || 1} link
                        </Badge>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-1 pt-2 border-t border-border/70 flex-wrap">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => setPreviewProposal(p)} data-testid={`proposal-preview-${p.id}`}>
                      <Eye className="w-3 h-3 mr-1" />{i18n.t('orders.preview', 'Anteprima')}
                    </Button>
                    {canEdit && (
                      <>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => openEdit(p)} data-testid={`proposal-edit-${p.id}`}>
                          <Pencil className="w-3 h-3 mr-1" />{i18n.t('common.edit', 'Modifica')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-[11px]"
                          onClick={() => duplicate(p)}
                          disabled={duplicating === p.id}
                          data-testid={`proposal-duplicate-${p.id}`}
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          {duplicating === p.id ? i18n.t('library.duplicating', 'Duplicazione…') : i18n.t('library.duplicate', 'Duplica')}
                        </Button>
                        {p.is_active && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-red-600 hover:text-red-700" onClick={() => setDeletingProposal(p)} data-testid={`proposal-delete-${p.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" />{i18n.t('library.deactivate', 'Disattiva')}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] p-0 flex flex-col"
          data-testid="proposal-editor-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle>{editing ? i18n.t('library.editProposal', 'Modifica proposta') : i18n.t('library.newProposal', 'Nuova proposta')}</DialogTitle>
            <DialogDescription>
              {i18n.t('library.editorDesc', 'Compila i dettagli. L\u2019email verrà inviata al lead quando il senior selezionerà questa proposta nel momento del cambio stage a \u201cProposta inviata\u201d.')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 px-6 py-4 overflow-y-auto flex-1" data-testid="proposal-editor-scroll">
            <div className="grid gap-2">
              <Label htmlFor="p-name" className="text-xs">{i18n.t('library.internalName', 'Nome interno')} *</Label>
              <Input id="p-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="proposal-form-name" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">{i18n.t('library.country', 'Paese')} *</Label>
                <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
                  <SelectTrigger data-testid="proposal-form-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.flag} {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{i18n.t('library.path', 'Path')} *</Label>
                <Select value={form.path} onValueChange={(v) => setForm((f) => ({ ...f, path: v }))}>
                  <SelectTrigger data-testid="proposal-form-path"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PATH_OPTIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="p-price" className="text-xs">{i18n.t('library.price', 'Prezzo (EUR)')} *</Label>
                <Input
                  id="p-price"
                  type="number" min="0" step="0.01"
                  value={form.price_eur}
                  onChange={(e) => setForm((f) => ({ ...f, price_eur: e.target.value }))}
                  data-testid="proposal-form-price"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="p-pay" className="text-xs">{i18n.t('library.paymentMethod', 'Metodo di pagamento')} *</Label>
              <Input id="p-pay" value={form.payment_method} onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))} placeholder={i18n.t('library.paymentPh', 'Es. Bonifico 100% saldo alla firma')} data-testid="proposal-form-payment" />
            </div>
            <div>
              <Label htmlFor="p-subj" className="text-xs">{i18n.t('library.emailSubject', 'Oggetto email')} *</Label>
              <Input id="p-subj" value={form.email_subject} onChange={(e) => setForm((f) => ({ ...f, email_subject: e.target.value }))} data-testid="proposal-form-subject" />
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <Label htmlFor="p-body" className="text-xs">{i18n.t('library.emailBody', 'Corpo email')} *</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Template selector (Opzione D) */}
                  {emailTemplates.length > 0 && (
                    <Select onValueChange={applyTemplate} value="">
                      <SelectTrigger className="h-8 w-[220px]" data-testid="proposal-template-select">
                        <div className="flex items-center gap-1 text-xs">
                          <Sparkles className="w-3 h-3" />
                          <SelectValue placeholder={i18n.t('library.tplPh', 'Template pre-fatti')} />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {emailTemplates.map((t) => (
                          <SelectItem key={t.key} value={t.key} data-testid={`proposal-template-item-${t.key}`}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {/* Import Google Doc (Opzione B) */}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => setImportDocOpen(true)}
                    data-testid="proposal-import-gdoc-btn"
                  >
                    <FileEdit className="w-3.5 h-3.5 mr-1" />{i18n.t('library.importGdoc', 'Importa da Google Doc')}
                  </Button>
                </div>
              </div>
              <RichEmailEditor
                value={form.email_body_html}
                onChange={(html) => setForm((f) => ({ ...f, email_body_html: html }))}
                placeholder={i18n.t('library.bodyPh', 'Scrivi il corpo dell\u2019email o usa un template dal menù qui sopra…')}
                minHeight="280px"
                testId="proposal-email-editor"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {i18n.t('library.placeholders', 'Placeholder supportati nel testo:')} <code>{'{{lead_name}}'}</code>, <code>{'{{lead_email}}'}</code>, <code>{'{{lead_phone}}'}</code>
              </p>
            </div>
            <div>
              <Label htmlFor="p-notes" className="text-xs">{i18n.t('library.internalNotes', 'Note interne (opzionale)')}</Label>
              <Textarea id="p-notes" rows={2} value={form.internal_notes} onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))} data-testid="proposal-form-notes" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="proposal-form-active" />
              {i18n.t('library.activeSwitch', 'Attiva (visibile ai senior quando creano un ordine)')}
            </label>

            {/* ★ 2026-08-10 (Fase 36) — Multi-PDF + Multi-link */}
            <div className="border-t pt-3 space-y-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4" />{i18n.t('library.attachedDocs', 'Documenti allegati (uno o più)')}
              </div>
              <p className="text-[11px] text-muted-foreground">{i18n.t('library.attachHint', 'Carica più PDF (max 10 MB ciascuno) e/o aggiungi più link esterni (Google Drive, Dropbox, ecc.). Tutti gli allegati verranno inclusi automaticamente nelle email della proposta (limite totale 20 MB in allegato binario; i link vengono inseriti nel corpo email).')}</p>

              {/* Lista PDF già caricati (solo in edit mode) */}
              {editing?.id && (editing.pdfs || []).length > 0 && (
                <div className="space-y-1.5" data-testid="proposal-pdf-list">
                  {(editing.pdfs || []).map((p) => (
                    <div key={p.id} className="flex items-center gap-2 p-2 rounded-md bg-emerald-50 border border-emerald-200 text-sm">
                      <FileText className="w-4 h-4 text-emerald-700 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.filename || 'proposta.pdf'}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {p.size ? `${(p.size / 1024 / 1024).toFixed(2)} MB` : ''}
                          {p.uploaded_at ? ` · ${i18n.t('library.uploadedOn', 'caricato')} ${new Date(p.uploaded_at).toLocaleDateString(dtLocale())}` : ''}
                        </div>
                      </div>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => removePdf(p.id)}
                        className="text-red-600 h-7 w-7 p-0"
                        data-testid={`proposal-pdf-remove-${p.id}`}
                        aria-label={i18n.t('orders.removeFile', 'Rimuovi {{name}}', { name: p.filename })}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Pending files (nuova proposta) */}
              {pendingPdfFiles.length > 0 && (
                <div className="space-y-1.5" data-testid="proposal-pdf-pending-list">
                  {pendingPdfFiles.map((f, idx) => (
                    <div key={`pending-${f.name}-${f.size}-${idx}`} className="flex items-center gap-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-sm">
                      <FileText className="w-4 h-4 text-amber-700 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{f.name}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {i18n.t('library.willUploadOnSave', 'Verrà caricato al salvataggio')} · {(f.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => setPendingPdfFiles((arr) => arr.filter((_, i) => i !== idx))}
                        className="text-red-600 h-7 w-7 p-0"
                        aria-label={i18n.t('library.removeFileGeneric', 'Rimuovi file')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload nuovo PDF (multi via input multiple) */}
              <label
                htmlFor="proposal-pdf-upload-input"
                className="flex items-center gap-2 p-2 rounded-md border border-dashed text-sm cursor-pointer hover:bg-slate-50 transition-colors"
              >
                <Upload className="w-4 h-4 text-slate-500" />
                <span className="flex-1">
                  {uploadingPdf ? i18n.t('common.loading', 'Caricamento…') : (
                    editing?.id
                      ? i18n.t('library.addPdf', 'Aggiungi PDF (uno o più, max 10 MB ciascuno)')
                      : i18n.t('library.selectPdf', 'Seleziona PDF (uno o più) — verranno caricati al salvataggio')
                  )}
                </span>
                <input
                  id="proposal-pdf-upload-input"
                  type="file"
                  accept="application/pdf"
                  multiple
                  className="hidden"
                  disabled={uploadingPdf}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    const valid = [];
                    for (const f of files) {
                      if (f.type !== 'application/pdf') {
                        toast.error(i18n.t('orders.notPdf', '"{{name}}" non è un PDF', { name: f.name }));
                        continue;
                      }
                      if (f.size > MAX_PDF_BYTES) {
                        toast.error(i18n.t('library.fileTooBigNamed', '"{{name}}" troppo grande (max 10 MB). Usa un link esterno.', { name: f.name }));
                        continue;
                      }
                      valid.push(f);
                    }
                    if (editing?.id) {
                      // Upload immediato uno per uno
                      valid.forEach((f) => uploadPdfNow(f));
                    } else {
                      // Accumula pending
                      setPendingPdfFiles((arr) => [...arr, ...valid]);
                    }
                    e.target.value = '';
                  }}
                  data-testid="proposal-pdf-upload"
                />
              </label>

              {/* Lista link esterni già associati (solo edit mode) */}
              {editing?.id && (editing.ext_links || []).length > 0 && (
                <div className="space-y-1.5" data-testid="proposal-ext-links-list">
                  {(editing.ext_links || []).map((el) => (
                    <div key={el.id} className="flex items-center gap-2 p-2 rounded-md bg-sky-50 border border-sky-200 text-sm">
                      <LinkIcon className="w-4 h-4 text-sky-700 shrink-0" />
                      <a
                        href={el.url} target="_blank" rel="noopener noreferrer"
                        className="flex-1 min-w-0 truncate text-sky-900 underline"
                      >
                        {el.url}
                      </a>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => removeExtLink(el.id)}
                        className="text-red-600 h-7 w-7 p-0"
                        data-testid={`proposal-ext-link-remove-${el.id}`}
                        aria-label={i18n.t('orders.removeLink', 'Rimuovi link')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Pending ext-links (nuova proposta) */}
              {pendingExtLinks.length > 0 && (
                <div className="space-y-1.5" data-testid="proposal-ext-links-pending">
                  {pendingExtLinks.map((url, idx) => (
                    <div key={`pending-link-${idx}`} className="flex items-center gap-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-sm">
                      <LinkIcon className="w-4 h-4 text-amber-700 shrink-0" />
                      <span className="flex-1 truncate">{url}</span>
                      <span className="text-[10px] text-muted-foreground">{i18n.t('library.willBeSaved', 'verrà salvato')}</span>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => setPendingExtLinks((arr) => arr.filter((_, i) => i !== idx))}
                        className="text-red-600 h-7 w-7 p-0"
                        aria-label={i18n.t('orders.removeLink', 'Rimuovi link')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Input aggiungi link */}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-[10px] uppercase">{i18n.t('library.addExtLink', 'Aggiungi link esterno (Drive/Dropbox)')}</Label>
                  <Input
                    value={newExtLinkInput}
                    onChange={(e) => setNewExtLinkInput(e.target.value)}
                    placeholder="https://drive.google.com/…"
                    className="mt-1 h-9"
                    data-testid="proposal-ext-link-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addExtLinkNow(); }
                    }}
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addExtLinkNow}
                  disabled={!newExtLinkInput.trim()}
                  className="h-9"
                  data-testid="proposal-ext-link-add"
                >
                  <Plus className="w-4 h-4 mr-1" />{i18n.t('orders.add', 'Aggiungi')}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-border shrink-0 bg-[var(--surface-2)]/40">
            <Button variant="outline" onClick={() => setEditorOpen(false)} disabled={saving}>{i18n.t('common.cancel', 'Annulla')}</Button>
            <Button onClick={save} disabled={saving} data-testid="proposal-form-save">
              {saving ? i18n.t('common.saving', 'Salvataggio…') : (editing ? i18n.t('library.saveChanges', 'Salva modifiche') : i18n.t('library.createProposal', 'Crea proposta'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewProposal} onOpenChange={(v) => !v && setPreviewProposal(null)}>
        <DialogContent className="max-w-2xl" data-testid="proposal-preview-dialog">
          <DialogHeader>
            <DialogTitle>{i18n.t('library.previewTitle', 'Anteprima email')}</DialogTitle>
            <DialogDescription>
              {i18n.t('orders.subject', 'Oggetto:')} <span className="font-medium">{previewProposal?.email_subject}</span>
            </DialogDescription>
          </DialogHeader>
          <SafeHtml
            html={previewProposal?.email_body_html || ''}
            className="prose prose-sm max-w-none border rounded-md p-4 bg-white overflow-auto max-h-[420px]"
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deletingProposal} onOpenChange={(v) => !v && setDeletingProposal(null)}>
        <AlertDialogContent data-testid="proposal-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{i18n.t('library.deactivateConfirmTitle', 'Disattivare questa proposta?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {i18n.t('library.deactivateConfirmDesc', '\u201c{{name}}\u201d verrà disattivata (soft-delete). Non apparirà più nelle selezioni, ma resterà collegata agli ordini storici. Puoi riattivarla in qualsiasi momento.', { name: deletingProposal?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{i18n.t('common.cancel', 'Annulla')}</AlertDialogCancel>
            <AlertDialogAction onClick={softDelete} className="bg-red-600 hover:bg-red-700">{i18n.t('library.deactivate', 'Disattiva')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ★ 2026-08-10 (Fase 35) — Import Google Doc dialog */}
      <Dialog open={importDocOpen} onOpenChange={(v) => !v && setImportDocOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="proposal-import-gdoc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="w-5 h-5 text-[hsl(var(--primary))]" />
              {i18n.t('library.importGdocTitle', 'Importa contenuto da Google Doc')}
            </DialogTitle>
            <DialogDescription>
              {i18n.t('library.importGdocDesc', 'Incolla il link del Google Doc pubblico. Il testo formattato verrà inserito nel corpo dell\u2019email.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              ⚠️ {i18n.t('library.gdocShareWarn', 'Importante: il documento deve avere condivisione \u201cChiunque con il link → Visualizzatore\u201d abilitata. Altrimenti l\u2019import fallirà con errore 403.')}
            </div>
            <div>
              <Label className="text-xs">{i18n.t('library.gdocUrl', 'URL del Google Doc')}</Label>
              <Input
                value={importDocUrl}
                onChange={(e) => setImportDocUrl(e.target.value)}
                placeholder="https://docs.google.com/document/d/…/edit"
                className="mt-1 font-mono text-xs"
                autoFocus
                data-testid="proposal-import-gdoc-url"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); importGoogleDoc(); } }}
              />
            </div>
            <details className="text-[11px] text-muted-foreground">
              <summary className="cursor-pointer hover:text-foreground">{i18n.t('library.gdocHowTitle', 'Come rendere il Google Doc pubblico?')}</summary>
              <ol className="list-decimal list-inside mt-2 space-y-1 pl-2">
                <li>{i18n.t('library.gdocHow1', 'Apri il Doc su Google Drive')}</li>
                <li>{i18n.t('library.gdocHow2', 'Clicca \u201cCondividi\u201d in alto a destra')}</li>
                <li>{i18n.t('library.gdocHow3', 'In \u201cAccesso generale\u201d seleziona \u201cChiunque abbia il link\u201d e imposta il ruolo su Visualizzatore')}</li>
                <li>{i18n.t('library.gdocHow4', 'Copia il link e incollalo qui sopra')}</li>
              </ol>
            </details>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDocOpen(false)} disabled={importingDoc}>
              {i18n.t('common.cancel', 'Annulla')}
            </Button>
            <Button onClick={importGoogleDoc} disabled={importingDoc || !importDocUrl.trim()} data-testid="proposal-import-gdoc-confirm">
              {importingDoc ? i18n.t('library.importing', 'Importazione…') : (<><FileEdit className="w-4 h-4 mr-1" />{i18n.t('library.import', 'Importa')}</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
